import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  martSearchItemsReducer,
  fetchMartSearchItems,
  getMartSearchResult,
  getMartSearchResultItems,
  getMartSearchResultProgress,
  getMartSearchResultQueryRewrite,
  getMartSearchResultTotal,
  getMartSearchResultNextOffset,
  processRawResponse,
  copyMartSearchResult,
  MartSearchItemsParams,
  CustomMartSearchItemsApi,
} from '@shopee/mart-search-items-redux';
import { InjectReducerContext } from '@shopee/with-inject-reducer';
import {
  getResultType,
  getQueryRewrittenSearchParams,
} from '@shopee/mart-search-util';
import getIn from '@shopee/get-in';
import {
  QUERY_REWRITE_TYPES,
  MART_SEARCH_RESULT_TYPE,
} from '@shopee/search-common-util';

// Types
import type { SearchParams } from '@shopee/search-common-util';
import type { Item } from '@shopee/item-card-sdk';
import type {
  MartSearchItemsStore,
  MartSearchItemsResponse,
  MartSearchQueryRewrite,
} from '@shopee/mart-search-items-redux';
import type { Dispatch } from 'redux';
import type { ProgressType } from '@shopee/api-progress';

// REACT_18_VIOLATION: Cannot export UUE hooks
export function useMartSearchItemsInfiniteLoading({
  reduxKey,
  searchParams,
  limit,
  getStore,
  onQueryRewrite,
  getCustomMartSearchItemsApi,
}: {
  reduxKey: string;
  searchParams: SearchParams;
  limit: number;
  getStore: (state: Record<string, unknown>) => MartSearchItemsStore | void;
  onQueryRewrite: (searchParams: SearchParams) => void;
  getCustomMartSearchItemsApi?: (
    params: MartSearchItemsParams
  ) => CustomMartSearchItemsApi | undefined;
}): {
  offset: number;
  progress: ProgressType;
  items: Item[];
  total: number;
  queryRewrite: MartSearchQueryRewrite | void;
  nextOffset: number;
  resultType: MART_SEARCH_RESULT_TYPE;
  handleLoadMore: () => void;
} {
  const { injectAsyncReducer } = React.useContext(InjectReducerContext);
  const dispatch = useDispatch<Dispatch<Promise<any>>>();

  const store = useSelector(getStore);
  const progress = getMartSearchResultProgress(store, searchParams);
  const items = getMartSearchResultItems(store, searchParams);
  const total = getMartSearchResultTotal(store, searchParams);
  const queryRewrite = getMartSearchResultQueryRewrite(store, searchParams);
  const nextOffset = getMartSearchResultNextOffset(store, searchParams);
  const searchResult = getMartSearchResult(store, searchParams);
  const resultType = getResultType({
    searchParams,
    searchResult: getMartSearchResult(store, searchParams),
  });

  /* eslint-disable max-statements */
  // NOTE: initial value is set to `nextOffset - limit` because
  // this hooks shouldn't fetch the next batch everytime it's triggered.
  // It should only fetch the next batch on load more.
  const [offset, setOffset] = React.useState(Math.max(nextOffset - limit, 0));

  React.useMemo(async () => {
    injectAsyncReducer({
      [reduxKey]: martSearchItemsReducer,
    });

    const rawResponse = await dispatch(
      fetchMartSearchItems(
        {
          searchParams,
          limit,
          offset,
          getStore,
        },
        getCustomMartSearchItemsApi
      )
    );

    if (offset !== 0) {
      return;
    }

    if (rawResponse) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { response, error } = rawResponse;
      if (!response || error) {
        return;
      }
      const rawResultType = getIn(response as MartSearchItemsResponse, [
        'data',
        'query_rewrite',
        'fe_query_write_status',
      ]);
      if (
        typeof rawResultType !== 'number' ||
        // @ts-expect-error fix later
        !QUERY_REWRITE_TYPES.includes(rawResultType)
      ) {
        return;
      }
      const searchResult = processRawResponse(
        response as MartSearchItemsResponse,
        offset,
        limit
      );
      const queryRewrite = searchResult.query_rewrite;
      const { rewrite_keyword: rewriteKeyword } = queryRewrite;
      if (!rewriteKeyword) {
        return;
      }
      const newParams = getQueryRewrittenSearchParams({
        searchParams,
        queryRewrite,
        resultType: rawResultType,
      });
      dispatch(copyMartSearchResult(newParams, searchResult));

      if (typeof onQueryRewrite === 'function') onQueryRewrite(newParams);
    } else if (searchResult && !searchParams.complementKeyword) {
      // NOTE: This is to handle the case when user revisit the url with query-rewritten keyword
      // Since we won't be fetching the API again for the same keyword, we have to handle
      // the url redirection manually here
      const queryRewrite = searchResult.query_rewrite;
      const { rewrite_keyword: rewriteKeyword } = queryRewrite;
      if (!rewriteKeyword) {
        return;
      }
      const newParams = getQueryRewrittenSearchParams({
        searchParams,
        queryRewrite,
        resultType,
      });

      if (typeof onQueryRewrite === 'function') onQueryRewrite(newParams);
    }
  }, [getStore, limit, offset, searchParams]);

  const handleLoadMore = React.useCallback(() => {
    setOffset(offset + limit);
  }, [offset, limit]);

  return {
    offset,
    progress,
    items,
    total,
    queryRewrite,
    nextOffset,
    resultType,
    handleLoadMore,
  };
}
/* eslint-enable max-statements */
